/**
 * Two layouts share one build and one server, so players on either can share a lobby:
 *  - Layout A: the standard game and lobby screens (the default).
 *  - Layout B: the round-table game and lobby screens.
 * The layout is a personal choice made from a drop-down in the lobby or the game. It is kept in this browser.
 * The old addresses still work: /GameA and /GameB open that layout (for that visit).
 * Every address, including the bare one, leads to the join / create lobby page.
 */
export const LAYOUT_A_PATH = "/GameA";
export const ROUND_TABLE_PATH = "/GameB";

const STORAGE_KEY = "sh-layout";

function pathLayout(): boolean | undefined {
  const m = /^\/game([ab])(\/|$)/i.exec(window.location.pathname);
  return m ? m[1].toLowerCase() === "b" : undefined;
}

function storedLayout(): boolean | undefined {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "B" ? true : v === "A" ? false : undefined;
  } catch (e) {
    return undefined; // storage can be blocked (private windows)
  }
}

function remember(round: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, round ? "B" : "A");
  } catch (e) {
    // The layout still changes for this visit.
  }
}

/** Set when the player switches layout while the page is open. */
let layoutOverride: boolean | undefined;

/** Layout B (round table)? The player's switch first, then a /GameA or /GameB address, then the stored choice. */
export function isRoundTable(): boolean {
  if (layoutOverride !== undefined) {
    return layoutOverride;
  }
  const fromPath = pathLayout();
  if (fromPath !== undefined) {
    return fromPath;
  }
  return storedLayout() ?? false;
}

/**
 * Switches this player between Layout A and Layout B without reloading the page, so a running game is not
 * interrupted. The choice is remembered for the next visit. On a /GameA or /GameB address that address is
 * updated too (same lobby), so a reload keeps the choice. It is personal: other players keep their own layout.
 */
export function setRoundTable(round: boolean): void {
  layoutOverride = round;
  remember(round);
  try {
    if (pathLayout() !== undefined) {
      const target = (round ? ROUND_TABLE_PATH : LAYOUT_A_PATH) + "/";
      window.history.replaceState(null, "", target + window.location.search + window.location.hash);
    }
  } catch (e) {
    // The address is a nicety; the layout has already changed.
  }
}

/**
 * The address to share for a lobby. It is the bare address: whoever opens it gets the layout they chose
 * themselves, not the layout of the person who shared it.
 */
export function lobbyLink(lobby: string): string {
  return window.location.origin + "/?lobby=" + lobby;
}
